import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { EmptyState, ErrorState, Kbd, Skeleton, type DropdownMenuItem } from '@/components/ui';
import { HierarchyRow, type HierarchyRowDragHandlers } from './HierarchyRow';
import { HierarchySearchResults } from './HierarchySearchResults';
import { buildLevelActions, buildOrgActions } from './hierarchyTreeActions';
import type { HierarchyPageVM } from './useHierarchyPage';
import type { DragSource, DragTarget, IllegalDropInfo } from './types';
import hz from './Hierarchy.module.css';
import styles from './HierarchyTreePanel.module.css';

export interface HierarchyTreePanelProps {
    vm: HierarchyPageVM;
}

/**
 * EAA-1804: pure module-level helpers, not closures re-declared inside the
 * component body every render. Both were previously plain functions defined
 * inline in `HierarchyTreePanel`, called ~3x per row per render — cheap
 * individually, but reallocated on every render (including every drag-frame
 * re-render) for no reason, since neither closes over anything the component
 * doesn't already have as explicit arguments.
 */
function targetIs(
    dragSource: DragSource | null,
    dragTarget: DragTarget | null,
    kind: 'org' | 'level',
    id: string,
    mode: 'before' | 'onto' | 'after'
): boolean {
    return (
        Boolean(dragSource) &&
        dragTarget?.kind === kind &&
        dragTarget.id === id &&
        dragTarget.mode === mode
    );
}

// Illegal styling belongs to the whole ROW being hovered, independent of
// before/onto/after — a Level dragged over an đơn vị is illegal no matter
// which third of that row's height the pointer sits in.
function illegalForRow(
    dragTarget: DragTarget | null,
    illegal: IllegalDropInfo | null,
    kind: 'org' | 'level',
    id: string
): IllegalDropInfo | null {
    return dragTarget?.kind === kind && dragTarget.id === id ? illegal : null;
}

function InsertionLine({ label }: { label: string }) {
    return (
        <span className={`${hz.rowWrap} ${styles.insertionWrap}`} aria-hidden="true">
            <span className={styles.insertionLine}>
                <span className={styles.insertionDot} />
                <span className={styles.insertionTrack} />
                <span className={styles.insertionChip}>{label}</span>
            </span>
        </span>
    );
}

/** Frame 33's "ĐANG TẢI" state — indented skeleton rows, not a bare spinner. */
function TreeSkeleton() {
    const { t } = useTranslation();
    return (
        <div className={hz.treeSkeleton} role="status" aria-live="polite">
            <span className="portal-sr-only">
                {t('console.hierarchy.treePanel.loadingSrLabel')}
            </span>
            {[0, 1, 2, 3].map((i) => (
                <span key={i} className={hz.skeletonRow} data-depth={i % 3}>
                    <Skeleton height="24px" width="24px" />
                    <Skeleton height="0.75rem" width={`${55 - i * 6}%`} />
                </span>
            ))}
        </div>
    );
}

/**
 * EAA-1818: left panel of `/console/hierarchy` — the lazily expanded
 * `Organization.parent` tree and the "LEVEL · TOÀN TỔ CHỨC, KHÔNG THEO ĐƠN VỊ"
 * group underneath it. The search input and "Thu gọn" button that used to open
 * this panel now live in `HierarchyPage`'s toolbar (frame 29); this panel only
 * reads `vm.searchActive` to decide whether `HierarchySearchResults` replaces
 * the tree body.
 *
 * The Level group is separate from the tree BY DESIGN, not by omission: a Level
 * belongs to the organization and is shared across every đơn vị in it, so it is
 * not a node and has no parent to drag it into (see `types.ts`).
 */
export function HierarchyTreePanel({ vm }: HierarchyTreePanelProps) {
    const { t } = useTranslation();
    const {
        canManage,
        canManageTree,
        visibleNodes,
        expandedIds,
        toggleExpanded,
        isLoadingRoot,
        isErrorRoot,
        rootOrgQuery,
        rootTruncated,
        visibleLevels,
        levelBreakdown,
        searchActive,
        selectedOrgId,
        setSelectedOrgId,
        dragSource,
        dragTarget,
        illegal,
        dropChipLabel,
        renamingId,
        renameDraft,
        setRenameDraft,
        renameError,
        beginRename,
        cancelRename,
        commitRename,
        renameOrg,
        openCreateChild,
        openMoveDialog,
        openAdoptDialog,
        setConfirmDeactivateOrgId,
        reactivateOrg,
        copyOrgCode,
        openRetype,
        unitTypesById,
        setConfirmDeactivateLevel,
        onRowDragStart,
        onRowDragOver,
        onRowDrop,
        onRowDragEnd,
        onTreeKeyDown,
        inlineDropError,
        setInlineDropError,
        moveError,
        dismissMoveError,
        pendingMove,
        movePending,
        completedMove,
        dismissCompletedMove,
        undoCompletedMove,
        undoPending,
        announcement,
    } = vm;

    const dragHandlers: HierarchyRowDragHandlers = useMemo(
        () => ({
            onDragStart: onRowDragStart,
            onDragOver: onRowDragOver,
            onDrop: onRowDrop,
            onDragEnd: onRowDragEnd,
            onKeyDown: onTreeKeyDown,
        }),
        [onRowDragStart, onRowDragOver, onRowDrop, onRowDragEnd, onTreeKeyDown]
    );

    /** The đơn vị whose re-parent PATCH is currently in flight, if any. */
    const movingOrgId = movePending ? (pendingMove?.node.id ?? null) : null;

    // EAA-1804: `reactivateOrg` (a `useMutation` result) is a fresh object
    // every render even though `.mutate` itself is a stable reference —
    // destructuring it up front keeps `.mutate` out of any dependency array
    // that would otherwise be invalidated on every render.
    const { mutate: reactivateOrgMutate } = reactivateOrg;

    /**
     * EAA-1804: per-row context-menu arrays, built ONCE per relevant
     * dependency change via `buildOrgActions`/`buildLevelActions`
     * (`./hierarchyTreeActions.ts`) rather than once per render for every
     * row. Before this, `orgActions(node)`/`levelActions(...)` ran inline in
     * the JSX below — an N-item `DropdownMenuItem[]` with 4-6 fresh
     * `onSelect` closures allocated per row on every single render
     * (including every drag-frame re-render), for a menu that is almost
     * always closed. Neither map is keyed on `dragSource`/`dragTarget`/
     * `illegal`, so a drag-hover re-render now skips rebuilding every row's
     * menu entirely.
     */
    const orgActionsById = useMemo(() => {
        const map = new Map<string, DropdownMenuItem[]>();
        visibleNodes.forEach((node) => {
            map.set(
                node.id,
                buildOrgActions({
                    node,
                    t,
                    canManage,
                    canManageTree,
                    onAddChild: openCreateChild,
                    onRename: beginRename,
                    onMove: openMoveDialog,
                    onAdopt: openAdoptDialog,
                    onCopyCode: copyOrgCode,
                    onRetype: openRetype,
                    onDeactivate: setConfirmDeactivateOrgId,
                    onReactivate: reactivateOrgMutate,
                })
            );
        });
        return map;
    }, [
        visibleNodes,
        t,
        canManage,
        canManageTree,
        openCreateChild,
        beginRename,
        openMoveDialog,
        openAdoptDialog,
        copyOrgCode,
        openRetype,
        setConfirmDeactivateOrgId,
        reactivateOrgMutate,
    ]);

    const levelActionsById = useMemo(() => {
        const map = new Map<string, DropdownMenuItem[]>();
        visibleLevels.forEach((level) => {
            map.set(level.id, buildLevelActions({ level, t, canManage, onDeactivate: setConfirmDeactivateLevel }));
        });
        return map;
    }, [visibleLevels, t, canManage, setConfirmDeactivateLevel]);

    /**
     * EAA-1804: the row-identity object (`row={{...}}`) and the three
     * per-row handlers (`onSelect`/`onRenameShortcut`/`onMoveShortcut`) used
     * to be fresh object/arrow literals allocated inline in the JSX map
     * below on every render — again including every drag-frame re-render,
     * even though `visibleNodes`, `canManage` and `canManageTree` almost
     * never change on those frames. Hoisted into one `useMemo`'d slice per
     * node id, keyed on the same low-churn dependencies as the action maps
     * above, so `HierarchyRow` (deliberately un-memoized, see its own
     * comment) at least receives stable handler references across
     * unrelated re-renders instead of new ones every time.
     */
    const orgRowSliceById = useMemo(() => {
        const map = new Map<
            string,
            {
                identity: { id: string; kind: 'org'; depth: number; label: string; code?: string };
                onSelect: () => void;
                onRenameShortcut?: () => void;
                onMoveShortcut?: () => void;
                onToggle: () => void;
            }
        >();
        visibleNodes.forEach((node) => {
            map.set(node.id, {
                identity: {
                    id: node.id,
                    kind: 'org',
                    depth: node.depth,
                    label: node.organization.name,
                    code: node.organization.code,
                },
                onSelect: () => setSelectedOrgId(node.id),
                onRenameShortcut: canManage ? () => beginRename(node) : undefined,
                onMoveShortcut:
                    canManageTree && node.depth > 0 ? () => openMoveDialog(node) : undefined,
                onToggle: () => toggleExpanded(node.id),
            });
        });
        return map;
    }, [
        visibleNodes,
        canManage,
        canManageTree,
        setSelectedOrgId,
        beginRename,
        openMoveDialog,
        toggleExpanded,
    ]);

    let treeBody: React.ReactNode;
    if (searchActive) {
        treeBody = <HierarchySearchResults vm={vm} />;
    } else if (isLoadingRoot) {
        treeBody = <TreeSkeleton />;
    } else if (isErrorRoot) {
        treeBody = (
            <ErrorState
                description={t('console.hierarchy.treePanel.loadErrorDescription')}
                onRetry={() => void rootOrgQuery.refetch()}
                retrying={rootOrgQuery.isFetching}
            />
        );
    } else if (visibleNodes.length === 0) {
        treeBody = (
            <EmptyState
                title={t('console.hierarchy.treePanel.emptyTitle')}
                description={t('console.hierarchy.treePanel.emptyDescription')}
            />
        );
    } else {
        treeBody = (
            <>
                {visibleNodes.map((node, i) => {
                    const next = visibleNodes[i + 1];
                    // EAA-1838: the badge resolves the org's `unit_type` id against
                    // the root's loaded vocabulary. An id with no matching row means
                    // the vocabulary has not arrived yet (or the type belongs to
                    // another root) — no chip is drawn rather than a raw uuid.
                    const unitType = node.organization.unit_type
                        ? unitTypesById.get(node.organization.unit_type)
                        : undefined;
                    const showInsertionAfter =
                        targetIs(dragSource, dragTarget, 'org', node.id, 'after') ||
                        (next ? targetIs(dragSource, dragTarget, 'org', next.id, 'before') : false);
                    // EAA-1804: pulled from the memoized per-node slice built above
                    // instead of allocating a fresh identity object / handler
                    // closures inline on every render — see the slice's own comment.
                    const rowSlice = orgRowSliceById.get(node.id);
                    return (
                        <React.Fragment key={node.id}>
                            {i === 0 && targetIs(dragSource, dragTarget, 'org', node.id, 'before') && (
                                <InsertionLine label={dropChipLabel} />
                            )}
                            <HierarchyRow
                                row={
                                    rowSlice?.identity ?? {
                                        id: node.id,
                                        kind: 'org',
                                        depth: node.depth,
                                        label: node.organization.name,
                                        code: node.organization.code,
                                    }
                                }
                                state={{
                                    // Frame 28 shows both pills. "đang chuyển"
                                    // wins while the PATCH is in flight, since
                                    // that is the more time-sensitive fact.
                                    badge:
                                        movingOrgId === node.id
                                            ? t('console.hierarchy.treePanel.badgeMoving')
                                            : node.organization.is_active
                                              ? undefined
                                              : t('console.hierarchy.treePanel.badgeInactive'),
                                    unitTypeLabel: unitType?.name,
                                    unitTypeHidden: unitType ? !unitType.is_active : undefined,
                                    statusDot: node.organization.is_active ? 'on' : 'off',
                                    muted: !node.organization.is_active,
                                    selected: selectedOrgId === node.id,
                                    moving: dragSource?.kind === 'org' && dragSource.id === node.id,
                                    ontoActive: targetIs(dragSource, dragTarget, 'org', node.id, 'onto'),
                                    illegal: illegalForRow(dragTarget, illegal, 'org', node.id),
                                }}
                                expand={{
                                    // Not "has children" — "not known to be a
                                    // leaf". Before the first fetch a node's
                                    // children are unknown, so the twisty must
                                    // still be offered (see types.ts).
                                    expandable: !node.childrenLoaded || node.children.length > 0,
                                    // Single source of truth, root included —
                                    // `useOrgTree` seeds the root into
                                    // `expandedIds` precisely so its twisty is
                                    // real rather than permanently "open".
                                    expanded: expandedIds.has(node.id),
                                    loading: node.childrenLoading,
                                    onToggle: rowSlice?.onToggle ?? (() => toggleExpanded(node.id)),
                                }}
                                rename={{
                                    active: renamingId === node.id,
                                    draft: renameDraft,
                                    error: renameError,
                                    pending: renameOrg.isPending,
                                    onDraftChange: setRenameDraft,
                                    onCommit: commitRename,
                                    onCancel: cancelRename,
                                }}
                                actions={orgActionsById.get(node.id) ?? []}
                                draggable={canManageTree && node.depth > 0}
                                onSelect={rowSlice?.onSelect ?? (() => setSelectedOrgId(node.id))}
                                onRenameShortcut={rowSlice?.onRenameShortcut}
                                onMoveShortcut={rowSlice?.onMoveShortcut}
                                dragHandlers={dragHandlers}
                            />
                            {showInsertionAfter && <InsertionLine label={dropChipLabel} />}
                        </React.Fragment>
                    );
                })}
                {rootTruncated && (
                    <p className={hz.cardNote}>
                        {t('console.hierarchy.treePanel.truncatedNote')}
                    </p>
                )}
            </>
        );
    }

    return (
        <div className="hz-shell">
            {/* EAA-1818: the search input and "Thu gọn" button moved up into
                `HierarchyPage`'s toolbar (frame 29 puts them next to the page title,
                not inside this panel). `searchQuery`/`setSearchQuery`/`collapseAll`
                now live only in the page shell — this panel still reads
                `searchActive` off the vm to decide whether `HierarchySearchResults`
                replaces the tree body below. */}

            {/* EAA-1818: the đơn vị tree and the Level list are TWO `role="tree"`
                regions inside one scroll container, not one. They are separate
                hierarchies (a Level is not a node of the org tree), and the
                search-result view that replaces the first region renders plain
                buttons — nesting those under a `role="tree"` would be invalid
                ARIA and would announce non-nodes as tree items. */}
            <div className={styles.tree}>
                <div
                    className={styles.treeGroup}
                    role={searchActive ? undefined : 'tree'}
                    aria-label={
                        searchActive ? undefined : t('console.hierarchy.treePanel.treeAriaLabel')
                    }
                >
                    {treeBody}
                </div>

                <div className={styles.levelGroupHeader}>
                    <span className={styles.levelGroupLabel}>
                        {t('console.hierarchy.treePanel.levelGroupLabel')}
                    </span>
                    <span className={styles.levelDivider} />
                </div>

                {visibleLevels.length === 0 && (
                    <EmptyState title={t('console.hierarchy.treePanel.noLevelsTitle')} />
                )}

                <div
                    className={styles.treeGroup}
                    role="tree"
                    aria-label={t('console.hierarchy.treePanel.levelsAriaLabel')}
                >
                    {visibleLevels.map((level, i) => (
                        <React.Fragment key={level.id}>
                            {targetIs(dragSource, dragTarget, 'level', level.id, 'before') && (
                                <InsertionLine
                                    label={t('console.hierarchy.treePanel.dropIntoLevelLabel')}
                                />
                            )}
                            <HierarchyRow
                                row={{
                                    id: level.id,
                                    kind: 'level',
                                    depth: 0,
                                    label: level.name,
                                    code: level.role_key,
                                }}
                                state={{
                                    countLabel: String(levelBreakdown.byLevelId[level.id] ?? 0),
                                    badge: level.is_active
                                        ? undefined
                                        : t('console.hierarchy.treePanel.badgeLevelPaused'),
                                    muted: !level.is_active,
                                    moving:
                                        dragSource?.kind === 'level' && dragSource.id === level.id,
                                    illegal: illegalForRow(dragTarget, illegal, 'level', level.id),
                                }}
                                actions={levelActionsById.get(level.id) ?? []}
                                dragHandlers={dragHandlers}
                            />
                            {i === visibleLevels.length - 1 &&
                                targetIs(dragSource, dragTarget, 'level', level.id, 'after') && (
                                    <InsertionLine
                                        label={t('console.hierarchy.treePanel.dropIntoLevelLabel')}
                                    />
                                )}
                        </React.Fragment>
                    ))}
                </div>

                {levelBreakdown.noLevel > 0 && (
                    <span className={hz.rowWrap}>
                        <span className={`${hz.row} ${hz.rowWarnStatic}`}>
                            <span className={hz.name}>
                                {t('console.hierarchy.treePanel.noLevelRowLabel')}
                            </span>
                            <span className={hz.count}>{levelBreakdown.noLevel}</span>
                        </span>
                    </span>
                )}
            </div>

            <HierarchyTreeFooter
                inlineDropError={inlineDropError ?? moveError}
                onDismissError={() => {
                    setInlineDropError(null);
                    dismissMoveError();
                }}
                completedMove={completedMove}
                onUndo={undoCompletedMove}
                onDismissCompleted={dismissCompletedMove}
                undoPending={undoPending}
            />

            <span className="portal-sr-only" role="status" aria-live="assertive">
                {announcement}
            </span>
        </div>
    );
}

interface HierarchyTreeFooterProps {
    inlineDropError: string | null;
    onDismissError: () => void;
    completedMove: { orgName: string; targetName: string } | null;
    onUndo: () => void;
    onDismissCompleted: () => void;
    undoPending: boolean;
}

/**
 * EAA-1818: the footer is one of three things — a failure, frame 29's success
 * banner, or the keyboard hint.
 *
 * The success copy deliberately does NOT repeat the mock's "N tài khoản đổi phạm
 * vi ứng dụng". Application scope lives per organization and is not inherited, so
 * a parent change grants and revokes nothing; claiming otherwise would be the one
 * thing on this screen that is untrue.
 */
function HierarchyTreeFooter({
    inlineDropError,
    onDismissError,
    completedMove,
    onUndo,
    onDismissCompleted,
    undoPending,
}: HierarchyTreeFooterProps) {
    const { t } = useTranslation();

    if (inlineDropError) {
        return (
            <div className={styles.footer}>
                <span className={styles.footerError} role="alert">
                    {inlineDropError}
                    <button
                        type="button"
                        className={styles.footerErrorDismiss}
                        onClick={onDismissError}
                    >
                        {t('common.close')}
                    </button>
                </span>
            </div>
        );
    }

    if (completedMove) {
        return (
            <div className={`${styles.footer} ${styles.footerSuccess}`} role="status">
                <span className={styles.footerSuccessBody}>
                    <span className={styles.footerSuccessTitle}>
                        {t('console.hierarchy.treePanel.moveSuccessTitle', {
                            name: completedMove.orgName,
                        })}
                    </span>
                    <span className={styles.footerSuccessNote}>
                        {t('console.hierarchy.treePanel.moveSuccessNote', {
                            target: completedMove.targetName,
                        })}
                    </span>
                </span>
                <button
                    type="button"
                    className={styles.footerUndo}
                    onClick={onUndo}
                    disabled={undoPending}
                >
                    {undoPending
                        ? t('console.hierarchy.treePanel.undoingLabel')
                        : t('console.hierarchy.treePanel.undoButton')}
                </button>
                <button
                    type="button"
                    className={styles.footerErrorDismiss}
                    onClick={onDismissCompleted}
                >
                    {t('common.close')}
                </button>
            </div>
        );
    }

    return (
        <div className={styles.footer}>
            <span className={styles.footerHint}>
                {/* EAA-1845: the hint interleaves plain text with <Kbd> chips, so it is
                    translated via <Trans> with named self-closing tags rather than a
                    plain t() call — a template-string interpolation would have to
                    serialize the <Kbd> elements to strings, losing their markup. */}
                <Trans
                    i18nKey="console.hierarchy.treePanel.keyboardHint"
                    components={{
                        space: <Kbd className={styles.footerKbd}>Space</Kbd>,
                        enter: <Kbd className={styles.footerKbd}>Enter</Kbd>,
                        esc: <Kbd className={styles.footerKbd}>Esc</Kbd>,
                        f2: <Kbd className={styles.footerKbd}>F2</Kbd>,
                        ctrl: <Kbd className={styles.footerKbd}>Ctrl</Kbd>,
                        kbdm: <Kbd className={styles.footerKbd}>M</Kbd>,
                    }}
                />
            </span>
        </div>
    );
}

export default HierarchyTreePanel;
